import React, { useState, useMemo, useEffect } from "react";
import TreatmentsFilter from "../../../components/treatments/TreatmentsFilter";
import TreatmentsKpi from "../../../components/treatments/TreatmentsKpi";
import TreatmentsAdd from "../../../components/treatments/TreatmentsAdd";
import TreatmentsCalendar from "../../../components/treatments/TreatmentsCalendar";
import TreatmentsKanban from "../../../components/treatments/TreatmentsKanban";
import DetailsTreatments from "../../../components/treatments/DetailsTreatments";
import TreatmentActionButton from "../../../components/treatments/TreatmentActionButton";
import { Link } from 'react-router-dom';
import DoctorManagementMenu from "../../../components/common/Menu/DoctorManagement";
import TopHeader from "../../../components/common/Header/TopHeader";

const Treatments = () => {
    const [isActionsMenuVisible, setIsActionsMenuVisible] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [view, setView] = useState("list");
    const [activeFilters, setActiveFilters] = useState({});
    const [allTreatments, setAllTreatments] = useState([]);
    const [filteredTreatments, setFilteredTreatments] = useState([]);
    const [kpiTableData, setKpiTableData] = useState(null);
    const [rowSelection, setRowSelection] = useState({});

    const toggleActionsMenu = () => {
        setIsActionsMenuVisible(!isActionsMenuVisible);
    };

    const handleSendMail = () => {
        const email = "example@domain.com";
        const subject = "Treatment Report";
        const body = "Hello, here is the treatment report.";
        window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    const handleViewChange = (newView) => {
        setView(newView);
    };

    const handleApplyFilters = (filtersData) => {
        setActiveFilters(filtersData);
        if (filtersData.patientName) {
            setSearchQuery(filtersData.patientName);
        }
    };

    const removeFilter = (key, value = null) => {
        setActiveFilters((prev) => {
            const updated = { ...prev };
            if (Array.isArray(updated[key])) {
                updated[key] = updated[key].filter((v) => v !== value);
                if (updated[key].length === 0) delete updated[key];
            } else {
                delete updated[key];
            }
            return updated;
        });

        if (key === 'patientName') {
            setSearchQuery("");
        }
    };

    const handleKpiDataUpdate = (kpiData) => {
        console.log('Received KPI data from table:', kpiData);
        setKpiTableData(kpiData);
        
        if (kpiData.allTreatments) {
            setAllTreatments(kpiData.allTreatments);
        }
        if (kpiData.visibleTreatments) {
            setFilteredTreatments(kpiData.visibleTreatments);
        }
    };

    const handleTreatmentDataUpdate = (componentName, treatments) => {
        if (componentName === 'DetailsTreatments') {
            setAllTreatments(treatments);
        }
    };

    // Callback para recibir la selección de filas desde DetailsTreatments
    const handleRowSelectionChange = (selection) => {
        setRowSelection(selection);
    };

    const applyFilters = useMemo(() => {
        let filtered = [...allTreatments];

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(treatment => {
                const searchableFields = [
                    treatment.treatment_info?.name,
                    treatment.treatment_info?.protocol_code,
                    treatment.patient_info?.patient_name,
                    treatment.patient_info?.patient_email,
                    treatment.doctor_info?.doctor_name,
                    treatment.doctor_info?.doctor_email,
                    treatment.doctor_info?.specialty,
                    treatment.treatment_info?.status,
                    treatment.treatment_id
                ];

                return searchableFields.some(field => 
                    field && String(field).toLowerCase().includes(query)
                );
            });
        }

        Object.entries(activeFilters).forEach(([filterKey, filterValue]) => {
            if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) return;

            switch (filterKey) {
                case 'patientName':
                    if (filterValue && filterValue.trim()) {
                        const query = filterValue.toLowerCase().trim();
                        filtered = filtered.filter(treatment => 
                            treatment.patient_info?.patient_name?.toLowerCase().includes(query)
                        );
                    }
                    break;

                case 'doctorName':
                    if (Array.isArray(filterValue)) {
                        filtered = filtered.filter(treatment => 
                            filterValue.some(doctor => 
                                treatment.doctor_info?.doctor_name?.toLowerCase().includes(doctor.toLowerCase())
                            )
                        );
                    } else if (filterValue) {
                        const query = filterValue.toLowerCase().trim();
                        filtered = filtered.filter(treatment => 
                            treatment.doctor_info?.doctor_name?.toLowerCase().includes(query)
                        );
                    }
                    break;

                case 'status':
                    if (Array.isArray(filterValue)) {
                        filtered = filtered.filter(treatment => 
                            filterValue.some(status => 
                                String(treatment.treatment_info?.status || '').toLowerCase() === status.toLowerCase()
                            )
                        );
                    } else if (filterValue) {
                        filtered = filtered.filter(treatment => 
                            String(treatment.treatment_info?.status || '').toLowerCase() === filterValue.toLowerCase()
                        );
                    }
                    break;

                case 'specialty':
                    if (Array.isArray(filterValue)) {
                        filtered = filtered.filter(treatment => 
                            filterValue.some(specialty => 
                                treatment.doctor_info?.specialty?.toLowerCase().includes(specialty.toLowerCase())
                            )
                        );
                    } else if (filterValue) {
                        filtered = filtered.filter(treatment => 
                            treatment.doctor_info?.specialty?.toLowerCase().includes(filterValue.toLowerCase())
                        );
                    }
                    break;

                case 'dateRange':
                    if (filterValue.startDate || filterValue.endDate) {
                        filtered = filtered.filter(treatment => {
                            const treatmentDate = new Date(treatment.treatment_info?.start_date);
                            let isInRange = true;

                            if (filterValue.startDate) {
                                const startDate = new Date(filterValue.startDate);
                                isInRange = isInRange && treatmentDate >= startDate;
                            }

                            if (filterValue.endDate) {
                                const endDate = new Date(filterValue.endDate);
                                isInRange = isInRange && treatmentDate <= endDate;
                            }

                            return isInRange;
                        });
                    }
                    break;

                case 'consentStatus':
                    if (Array.isArray(filterValue)) {
                        filtered = filtered.filter(treatment => {
                            return filterValue.some(consent => {
                                if (consent === 'signed') {
                                    return treatment.consent_status?.is_signed === true;
                                } else if (consent === 'pending') {
                                    return treatment.consent_status?.is_required && 
                                           treatment.consent_status?.is_signed === false;
                                } else if (consent === 'not_required') {
                                    return !treatment.consent_status?.is_required;
                                }
                                return false;
                            });
                        });
                    }
                    break;

                default:
                    break;
            }
        });

        return filtered;
    }, [allTreatments, searchQuery, activeFilters]);

    useEffect(() => {
        if (view !== "list") {
            setFilteredTreatments(applyFilters);
        }
    }, [applyFilters, view]);

    return (
        <main id="app">
            <TopHeader />
            <div className="content_wrapper">
                <section className="doctor-management-wrapper bg-white pb-5">
                    <DoctorManagementMenu activeMenu='/treatmentList' />
                    <div className="container-fluid px-lg-5">

                        {/* Breadcrumb */}
                        <nav className="breadcrumb-container d-none d-md-block pt-3 mb-2" aria-label="breadcrumb">
                            <ol className="breadcrumb fs-sm mb-0">
                                <li className="breadcrumb-item"><Link to="#">Dashboard</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Treatments</li>
                            </ol>
                        </nav>

                        <TreatmentsKpi 
                            tableData={kpiTableData}
                            treatments={allTreatments} 
                            filteredTreatments={filteredTreatments}
                        />

                        <div className="filter-container py-3">
                            <div className="row align-items-center">
                                {/* View Options */}
                                <div className="col-md-5 col-lg-6 d-none d-md-block mb-3 mb-md-0">
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${view === "calendar" ? "btn-primary" : "btn-light text-secondary"}`}
                                        onClick={() => handleViewChange("calendar")}
                                    >
                                        Calendar
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${view === "list" ? "btn-primary" : "btn-light text-secondary"}`}
                                        onClick={() => handleViewChange("list")}
                                    >
                                        List
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${view === "kanban" ? "btn-primary" : "btn-light text-secondary"}`}
                                        onClick={() => handleViewChange("kanban")}
                                    >
                                        Kanban
                                    </button>
                                

                                    {/* Right Controls */}
                                    {/* Search input - solo mostrar en vista de lista */}
                                    {view === "list" && (
                                        <div className="form-group d-flex align-items-center flex-row-reverse position-relative">
                                            <input
                                                type="text"                                                    
                                                placeholder="Search treatments, patients, doctors..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)} 
                                                className="form-control ps-2 bg-transparent border-0 shadow-none"
                                            />
                                            <button type="submit" className="p-0 bg-transparent border-0">
                                                <span className="d-block material-symbols-outlined icon-lg">search</span>
                                            </button>
                                        </div>                                        
                                    )}
                                </div>

                                {/* Actions Menu */}
                                <div className="col-md-7 col-lg-6 d-flex justify-content-end gap-1 gap-sm-2 gy-1 flex-wrap">
                                    <TreatmentActionButton 
                                        rowSelection={rowSelection} 
                                        filteredTreatments={filteredTreatments}
                                    />

                                    <TreatmentsFilter 
                                        onApplyFilters={handleApplyFilters} 
                                        treatments={allTreatments}
                                    />
                                    <TreatmentsAdd />
                                </div>                                
                            </div>
                        </div>

                        {/* Active filters display */}
                        {view !== "list" && Object.keys(activeFilters).length > 0 && (
                            <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
                                <span className="text-muted fw-semibold small">Active Filters</span>
                                {Object.entries(activeFilters).map(([key, value]) => {
                                    if (!value || (Array.isArray(value) && value.length === 0)) return null;

                                    if (Array.isArray(value)) {
                                        return value.map((v, i) => (
                                            <span
                                                key={`${key}-${i}`}
                                                className="badge bg-white border text-dark d-inline-flex align-items-center px-2 py-1 rounded-pill shadow-sm"
                                                style={{ fontSize: '0.75rem', fontWeight: '500' }}
                                            >
                                                <span className="me-2">{key.charAt(0).toUpperCase() + key.slice(1)}: {v}</span>
                                                <button
                                                    type="button"
                                                    className="btn-close"
                                                    style={{ fontSize: "0.5rem" }}
                                                    onClick={() => removeFilter(key, v)}
                                                ></button>
                                            </span>
                                        ));
                                    }

                                    if (key === 'dateRange' && typeof value === 'object') {
                                        return (
                                            <span
                                                key={key}
                                                className="badge bg-white border text-dark d-inline-flex align-items-center px-2 py-1 rounded-pill shadow-sm"
                                                style={{ fontSize: '0.75rem', fontWeight: '500' }}
                                            >
                                                <span className="me-2">
                                                    Date: {value.startDate ? new Date(value.startDate).toLocaleDateString() : ''} - 
                                                    {value.endDate ? new Date(value.endDate).toLocaleDateString() : ''}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="btn-close"
                                                    style={{ fontSize: "0.5rem" }}
                                                    onClick={() => removeFilter(key)}
                                                ></button>
                                            </span>
                                        );
                                    }

                                    return (
                                        <span
                                            key={key}
                                            className="badge bg-white border text-dark d-inline-flex align-items-center px-2 py-1 rounded-pill shadow-sm"
                                            style={{ fontSize: '0.75rem', fontWeight: '500' }}
                                        >
                                            <span className="me-2">{key.charAt(0).toUpperCase() + key.slice(1)}: {value}</span>
                                            <button
                                                type="button"
                                                className="btn-close"
                                                style={{ fontSize: "0.5rem" }}
                                                onClick={() => removeFilter(key)}
                                            ></button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}

                        {/* Render views based on selection */}
                        {view === "calendar" && (
                            <TreatmentsCalendar 
                                treatments={filteredTreatments}
                            />
                        )}
                        {view === "list" && (
                            <DetailsTreatments 
                                onCountUpdate={handleTreatmentDataUpdate}
                                onKpiDataUpdate={handleKpiDataUpdate}
                                onRowSelectionChange={handleRowSelectionChange}
                                componentName="DetailsTreatments"
                                externalSearchQuery={searchQuery}
                                externalFilters={activeFilters}
                            />
                        )}
                        {view === "kanban" && (
                            <TreatmentsKanban 
                                treatments={filteredTreatments}
                            />
                        )}                       
                        
                    </div>
                </section>
            </div>
        </main>
    );
};

export default Treatments;